/**
 * Money, exactly as the user typed it — the owner's rule (2026-10-02).
 *
 * A price or cost keeps up to ten decimals, and nothing the app stores or adds
 * up is ever rounded: $0.005 a tablet is $0.005, and a hundred of them is
 * exactly $0.50. Rounding happens only on the way to a person's eyes (see
 * `showMoney`), never on the way to the database.
 *
 * A `Money` is decimal text — "2.5", "0.00000001", "-12" — because text
 * crosses every boundary untouched: JSON to and from the phone, SQLite on it,
 * and Postgres `numeric`, which keeps it exactly. Arithmetic goes through the
 * functions here, which work in whole ten-billionths of a dollar as BigInt,
 * so 0.1 + 0.2 is 0.3 and not 0.30000000000000004.
 *
 * The brand stops two Money values being joined with `+`, which on text would
 * glue them together ("1.5" + "2" is "1.52") rather than add them.
 */

export type Money = string & { readonly __money: unique symbol };

/** The owner's rule: up to ten decimals. */
export const MONEY_DECIMALS = 10;

/** Digits before the point the database keeps: numeric(28, 10). A quintillion dollars. */
export const MONEY_WHOLE_DIGITS = 18;

const SCALE = BigInt("10000000000"); // 10^MONEY_DECIMALS
const ZERO_UNITS = BigInt(0);
const TWO = BigInt(2);

export const ZERO = "0" as Money;

/* ---------------------------------------------------------------- reading */

/**
 * Sign, whole part and decimals, or null when it is not an amount. Allows "12",
 * "12.", ".5", "-3.25" — a field mid-typing as well as a finished one.
 */
function split(text: string): { negative: boolean; whole: string; fraction: string } | null {
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;
  const [, sign, wholeDigits = "", fraction = ""] = match;
  if (wholeDigits === "" && fraction === "") return null;
  if (fraction.length > MONEY_DECIMALS) return null;
  const whole = wholeDigits.replace(/^0+(?=\d)/, "") || "0";
  if (whole.length > MONEY_WHOLE_DIGITS) return null;
  return { negative: sign === "-", whole, fraction };
}

function unitsOf(parts: { negative: boolean; whole: string; fraction: string }): bigint {
  const units = BigInt(parts.whole + parts.fraction.padEnd(MONEY_DECIMALS, "0"));
  return parts.negative ? -units : units;
}

/** Whole ten-billionths of a dollar. */
function toUnits(value: Money): bigint {
  const parts = split(value);
  if (!parts) throw new Error(`Not money: "${value}"`);
  return unitsOf(parts);
}

/** Back to text, in its shortest form: "2.5", "0.00000001", "0". Never "-0". */
function fromUnits(units: bigint): Money {
  const negative = units < ZERO_UNITS;
  const abs = negative ? -units : units;
  const whole = (abs / SCALE).toString();
  const fraction = (abs % SCALE).toString().padStart(MONEY_DECIMALS, "0").replace(/0+$/, "");
  const body = fraction ? `${whole}.${fraction}` : whole;
  return (negative ? `-${body}` : body) as Money;
}

/**
 * What somebody typed, as money. Null when it is not an amount at all — empty,
 * letters, more than ten decimals — so a field can say why Save stays grey.
 *
 * Null rather than zero: an empty field and a price of zero are different
 * answers, and free goods are real. Accepts "12", "12.5", "$12.50",
 * "1,250.005" and "0.0000000001".
 */
export function readMoney(input: string): Money | null {
  const parts = split(input.trim().replace(/[$\s,]/g, ""));
  return parts ? fromUnits(unitsOf(parts)) : null;
}

/** Whether text is an amount as it travels: plain digits, at most ten decimals. */
export function isMoneyText(text: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(text) && split(text) !== null;
}

/**
 * Money from a value the code already holds — a stored amount, a test's
 * figure. Throws on anything that is not one: that is a bug, not a typo.
 */
export function money(value: string | number): Money {
  const text = typeof value === "number" ? String(value) : value;
  const parts = typeof value === "number" && !Number.isFinite(value) ? null : split(text);
  if (!parts) throw new Error(`Not money: "${text}"`);
  return fromUnits(unitsOf(parts));
}

/* ------------------------------------------------------------- arithmetic */

export function plus(a: Money, b: Money): Money {
  return fromUnits(toUnits(a) + toUnits(b));
}

export function minus(a: Money, b: Money): Money {
  return fromUnits(toUnits(a) - toUnits(b));
}

export function negate(a: Money): Money {
  return fromUnits(-toUnits(a));
}

/** Everything added up. Nothing is zero. */
export function sumMoney(values: Iterable<Money>): Money {
  let units = ZERO_UNITS;
  for (const value of values) units += toUnits(value);
  return fromUnits(units);
}

/**
 * A price times how many: exact, since a count is a whole number. 100 tablets
 * at $0.005 is $0.5, not a cent more or less.
 */
export function times(a: Money, count: number): Money {
  if (!Number.isSafeInteger(count)) throw new Error(`Not a whole count: ${count}`);
  return fromUnits(toUnits(a) * BigInt(count));
}

/**
 * Shared out over a whole count: a box's cost over its tablets, a total over
 * the units it bought. The one place a figure may not end — $1 over 3 is
 * 0.333… — so it stops at the tenth decimal, cut and never rounded up.
 */
export function dividedBy(a: Money, count: number): Money {
  if (!Number.isSafeInteger(count) || count <= 0) throw new Error(`Not a count to share over: ${count}`);
  // BigInt division cuts toward zero.
  return fromUnits(toUnits(a) / BigInt(count));
}

/**
 * How many whole units an amount buys, to the NEAREST unit (a half rounds up),
 * and never fewer than one: "$1 of sugar" at $0.15 a tumun is 7 tumun. Used
 * for a shop's sale by money, which charges exactly the amount asked; the
 * shopkeeper weighs by eye (2026-10-27). Exact — no computer decimals.
 */
export function nearestUnitsFor(amount: Money, unitPrice: Money): number {
  const per = toUnits(unitPrice);
  if (per <= ZERO_UNITS) throw new Error(`Not a price to share by: ${unitPrice}`);
  const units = (toUnits(amount) * TWO + per) / (per * TWO);
  return Math.max(1, Number(units));
}

/** -1, 0 or 1. Never compare Money text with < or >: "10" < "9" as text. */
export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  const x = toUnits(a);
  const y = toUnits(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export const sameMoney = (a: Money, b: Money): boolean => compareMoney(a, b) === 0;
export const isZero = (a: Money): boolean => toUnits(a) === ZERO_UNITS;
export const isNegative = (a: Money): boolean => toUnits(a) < ZERO_UNITS;
export const isPositive = (a: Money): boolean => toUnits(a) > ZERO_UNITS;

export function maxMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) >= 0 ? a : b;
}

export function minMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) <= 0 ? a : b;
}

/* ---------------------------------------------------------------- showing */

/** Shown: up to four decimals, as the owner chose. */
const SHOWN_DECIMALS = 4;
/** Ten-billionths in one ten-thousandth: what is rounded away for showing. */
const SHOWN_STEP = BigInt("1000000"); // 10^(MONEY_DECIMALS - SHOWN_DECIMALS)

export interface ShowMoney {
  /** Prepended when present. "$" for a price, nothing inside a column with a heading. */
  symbol?: string;
}

/**
 * Money for a person to read, as the owner chose:
 *
 *   - at least two decimals, as money usually looks: $2.50, $0.15;
 *   - up to four, rounded the normal way — five and above goes up — so a
 *     worked-out cost of 0.3298374834 reads $0.3298;
 *   - an amount that would then look like nothing is shown in full instead:
 *     $0.00000001, never $0.00;
 *   - thousands separated: $120,000.50.
 *
 * Only what is shown is rounded. What is stored and added up never is.
 */
export function showMoney(value: Money, { symbol = "$" }: ShowMoney = {}): string {
  const units = toUnits(value);
  const negative = units < ZERO_UNITS;
  const abs = negative ? -units : units;

  let steps = abs / SHOWN_STEP;
  if ((abs % SHOWN_STEP) * BigInt(2) >= SHOWN_STEP) steps += BigInt(1);

  let whole: string;
  let fraction: string;
  if (steps === ZERO_UNITS && abs !== ZERO_UNITS) {
    // Too small for four decimals: in full.
    const exact = fromUnits(abs);
    [whole, fraction = ""] = exact.split(".") as [string, string?];
  } else {
    const perWhole = BigInt(10 ** SHOWN_DECIMALS);
    whole = (steps / perWhole).toString();
    fraction = (steps % perWhole).toString().padStart(SHOWN_DECIMALS, "0").replace(/0+$/, "");
  }

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = `${grouped}.${fraction.padEnd(2, "0")}`;
  return `${negative ? "-" : ""}${symbol}${body}`;
}

/**
 * Money put back into a field to be changed: the full saved value, never the
 * rounded one — so saving it again loses nothing. "2.50", "0.3298374834".
 */
export function moneyForInput(value: Money): string {
  const [whole, fraction = ""] = value.split(".") as [string, string?];
  return `${whole}.${fraction.padEnd(2, "0")}`;
}

/**
 * The price of one smallest unit, given a size's price and how many it holds:
 * the figure that tells a pharmacist whether the strip or the box is better
 * value. Shared out exactly — see `dividedBy`.
 */
export function pricePerUnit(sizePrice: Money, multiplier: number): Money {
  return multiplier < 1 ? sizePrice : dividedBy(sizePrice, multiplier);
}
